import { useCallback, useEffect, useRef, useState, type FocusEvent, type PointerEvent } from 'react';

/**
 * S296 (kullanıcı 2026-09-23): Gizova'nın yan menüsü de kapalı başlayıp fareyle açılsın. Yerlice'deki
 * kancanın (`RaporGor.CustomerPanel.Yerlice/src/hooks/useRayUzerineAcilma.ts`) AYNI kopyası: iki panel ayrı
 * paket, ortak kod yeri yok. Davranış değişirse ikisi birlikte değişmeli. Aşağıdaki not Yerlice'nindir;
 * Template'te "kütüphanenin `collapsed`ı" yerine Layout'un `collapsed` state'i, `dashboard.css` yerine
 * Layout'taki Tailwind sınıfları geçerli.
 *
 * S263: yönetici yan menüsü HEP DARALTILMIŞ durur, fare kenara gelince ÜSTÜNE BİNEREK açılır.
 *
 * Kullanıcı notu (2026-09-11): "SideNav hep daraltılmış (collapsed); fare o kenara gidince
 * otomatik açılsın." Kararlar: menü içeriğin ÜSTÜNE biner (tablo kıpırdamaz) · ≡ düğmesi
 * masaüstünde kalır ve SABİTLER · kapsam yalnız yönetici (saha S152'den beri açık başlar).
 *
 * 🔴 Bu kanca kütüphanenin `collapsed` durumuna DOKUNMAZ; ayrı bir "hover ile açık" işareti
 * tutar. `collapsed` çevrilseydi ızgara sütunu genişler ve içerik İTİLİRDİ; üstelik
 * kütüphane açık hâlde başlıkta marka adını ve menüde "Menü" grup başlığını çizer; ikisi de
 * imlecin altındaki öğeleri kaydırırdı. Görüntüyü `dashboard.css` → `[data-ray-acik]` çizer.
 *
 * 🔴 YALNIZ GERÇEK FARE (`pointerType === 'mouse'`). Dokunmatik ekran bir dokunuşta
 * `pointerover`/`mouseover` da üretir; kontrol olmasa tablette ikona dokunmak menüyü açar
 * ve menü gezinmenin ÜSTÜNE binerdi. Klavye ise odakla açar (Tab ile gezen görsün).
 *
 * Gecikmeler hover NİYETİ içindir: kenardan geçen fare menüyü çırpındırmasın (açılma),
 * menüden taşan kısa bir sapma menüyü kapatmasın (kapanma).
 */
export const RAY_ACILMA_MS = 150;
export const RAY_KAPANMA_MS = 300;

export interface RayUzerineAcilma {
  /** Ray şu an hover/odakla açık mı (`data-ray-acik`). */
  acik: boolean;
  /** Bekleyen zamanlayıcıyı iptal edip rayı hemen kapatır (≡ ile sabitleme/bırakma). */
  kapat: () => void;
  /** Kenar çubuğu kabına (aside) yayılır. */
  baglar: {
    onPointerEnter: (e: PointerEvent<HTMLElement>) => void;
    onPointerLeave: () => void;
    onFocus: () => void;
    onBlur: (e: FocusEvent<HTMLElement>) => void;
  };
}

/** @param etkin Ray daraltılmış VE hover işi bu rolde açık mı (yönetici + sabitlenmemiş). */
export function useRayUzerineAcilma(etkin: boolean): RayUzerineAcilma {
  const [acik, setAcik] = useState(false);
  const zamanlayici = useRef<ReturnType<typeof setTimeout> | null>(null);

  const iptal = useCallback(() => {
    if (zamanlayici.current !== null) {
      clearTimeout(zamanlayici.current);
      zamanlayici.current = null;
    }
  }, []);

  const planla = useCallback((deger: boolean, ms: number) => {
    iptal();
    zamanlayici.current = setTimeout(() => {
      zamanlayici.current = null;
      setAcik(deger);
    }, ms);
  }, [iptal]);

  // Söküldüğünde bekleyen zamanlayıcı sökülmüş bileşene state yazmasın.
  useEffect(() => iptal, [iptal]);

  const kapat = useCallback(() => {
    iptal();
    setAcik(false);
  }, [iptal]);

  return {
    acik,
    kapat,
    baglar: {
      onPointerEnter: (e) => {
        if (!etkin || e.pointerType !== 'mouse') return;
        planla(true, RAY_ACILMA_MS);
      },
      // Ayrılışta tür kapısı YOK: ray dokunmayla hiç açılmıyor; bir dokunmanın ayrılışının
      // (klavyeyle açılmış) rayı kapatması zararsız, hover katmanı zaten fareye ait.
      onPointerLeave: () => {
        planla(false, RAY_KAPANMA_MS);
      },
      onFocus: () => {
        if (!etkin) return;
        iptal();
        setAcik(true);
      },
      // Ray İÇİNDE bir bağdan ötekine geçiş kapatmaz; yalnız odak dışarı çıkınca.
      // 🔴 Bu kontrol GEREKLİ. Tarayıcı `focusout` ile `focusin`i AYRI olaylar olarak gönderir
      // ve React her birinin ardından ayrı render eder (ölçüldü, 1440px: kontrolsüz sürümde
      // rayda üç Tab = işaret ÜÇ KEZ düştü). jsdom'da `act()` ikisini birleştirdiği için test
      // bunu ancak iki olayı ayrı gönderince görür (AppLayout.test.tsx).
      onBlur: (e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        kapat();
      },
    },
  };
}
